(() => {
  const selector = '[data-hero-slider-component]';
  const instances = new WeakMap();

  class HeroSlider {
    constructor(root) {
      if (instances.has(root)) return instances.get(root);
      this.root = root;
      this.slides = [...root.querySelectorAll('[data-hero-slide-item]')];
      this.bullets = [...root.querySelectorAll('[data-hero-pagination]')];
      this.previous = root.querySelector('[data-hero-previous]');
      this.next = root.querySelector('[data-hero-next]');
      this.status = root.querySelector('[data-hero-status]');
      this.index = Math.max(0, this.slides.findIndex(slide => slide.classList.contains('is-active')));
      this.delay = Number(root.dataset.autoplayDelay) || 6000;
      this.progressDuration = Number(root.dataset.progressDuration) || 7000;
      this.autoplay = root.dataset.enableSlider === 'true' && this.slides.length > 1;
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.editorPaused = false;
      this.timer = 0;
      this.transitionTimer = 0;
      this.abort = new AbortController();
      this.root.style.setProperty('--hero-progress-duration', `${this.progressDuration}ms`);
      this.bind();
      this.show(this.index, { instant: true, announce: false });
      this.schedule();
      instances.set(root, this);
    }

    bind() {
      const { signal } = this.abort;
      this.previous?.addEventListener('click', () => this.show(this.index - 1), { signal });
      this.next?.addEventListener('click', () => this.show(this.index + 1), { signal });
      this.bullets.forEach((bullet, index) => bullet.addEventListener('click', () => this.show(index), { signal }));
      this.slides.forEach((slide, index) => {
        slide.querySelectorAll('video').forEach(video => {
          video.addEventListener('ended', () => {
            if (this.autoplay && index === this.index && !this.editorPaused && !this.reducedMotion.matches) {
              this.show(index + 1);
            }
          }, { signal });
          video.addEventListener('loadedmetadata', () => {
            if (index === this.index) {
              this.syncProgressDuration(video);
              this.restartProgress();
            }
          }, { signal });
        });
      });
      this.root.addEventListener('focusin', event => {
        const slide = event.target.closest('[data-hero-slide-item]');
        if (slide && !slide.classList.contains('is-active')) this.show(this.slides.indexOf(slide));
      }, { signal });
      document.addEventListener('visibilitychange', () => document.hidden ? this.clearTimer() : this.schedule(), { signal });
      this.reducedMotion.addEventListener?.('change', () => {
        if (this.reducedMotion.matches) this.clearTimer();
        else this.schedule();
      }, { signal });
    }

    show(requestedIndex, options = {}) {
      if (!this.slides.length) return;
      const nextIndex = (requestedIndex + this.slides.length) % this.slides.length;
      if (nextIndex === this.index && !options.instant) {
        this.restartProgress();
        this.schedule();
        return;
      }

      clearTimeout(this.transitionTimer);
      this.index = nextIndex;
      this.slides.forEach((slide, index) => {
        const active = index === nextIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.toggleAttribute('inert', !active);
        slide.querySelectorAll('video').forEach(video => {
          if (active) {
            video.currentTime = 0;
            video.play().catch(() => {});
          } else video.pause();
        });
      });
      this.bullets.forEach((bullet, index) => {
        const active = index === nextIndex;
        bullet.classList.toggle('is-active', active);
        bullet.setAttribute('aria-current', String(active));
      });
      if (this.status && options.announce !== false) {
        this.status.textContent = `Slide ${nextIndex + 1} of ${this.slides.length}`;
      }
      this.syncProgressDuration(this.activeVideo());
      this.animateActive(options.instant || this.reducedMotion.matches);
      this.restartProgress();
      this.schedule();
    }

    animateActive(instant) {
      const slide = this.slides[this.index];
      const media = slide?.querySelector('[data-hero-media]');
      const items = slide ? [...slide.querySelectorAll('[data-hero-motion]')] : [];
      if (!slide || instant || !Element.prototype.animate) return;
      media?.animate(
        [{ transform: 'scale(1.08)' }, { transform: 'scale(1)' }],
        { duration: 1500, easing: 'cubic-bezier(0, 0, .3, 1)', fill: 'both' }
      );
      items.forEach((item, index) => item.animate(
        [{ opacity: .01, transform: 'translateY(25px)' }, { opacity: 1, transform: 'translateY(0)' }],
        { delay: 50 + index * 50, duration: 500, easing: 'cubic-bezier(0, 0, .3, 1)', fill: 'both' }
      ));
    }

    restartProgress() {
      this.root.classList.add('is-resetting-progress');
      requestAnimationFrame(() => requestAnimationFrame(() => this.root.classList.remove('is-resetting-progress')));
    }

    activeVideo() {
      return this.slides[this.index]?.querySelector('video') || null;
    }

    syncProgressDuration(video) {
      const duration = video && Number.isFinite(video.duration) ? video.duration * 1000 : this.progressDuration;
      this.root.style.setProperty('--hero-progress-duration', `${duration}ms`);
    }

    schedule() {
      this.clearTimer();
      if (!this.autoplay || this.editorPaused || document.hidden || this.reducedMotion.matches) return;
      const video = this.activeVideo();
      if (video) return;
      this.timer = window.setTimeout(() => this.show(this.index + 1), this.delay);
    }

    clearTimer() {
      clearTimeout(this.timer);
      this.timer = 0;
    }

    selectBlock(block) {
      const index = this.slides.indexOf(block.closest('[data-hero-slide-item]'));
      if (index < 0) return;
      this.editorPaused = true;
      this.clearTimer();
      this.show(index, { instant: true });
      this.clearTimer();
    }

    resumeEditor() {
      this.editorPaused = false;
      this.schedule();
    }

    destroy() {
      this.clearTimer();
      clearTimeout(this.transitionTimer);
      this.abort.abort();
      instances.delete(this.root);
    }
  }

  const init = scope => scope.querySelectorAll(selector).forEach(root => new HeroSlider(root));
  const instanceFor = target => {
    const root = target.closest(selector);
    return root ? instances.get(root) : null;
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);

  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll(selector).forEach(root => instances.get(root)?.destroy()));
  document.addEventListener('shopify:block:select', event => instanceFor(event.target)?.selectBlock(event.target));
  document.addEventListener('shopify:block:deselect', event => instanceFor(event.target)?.resumeEditor());
})();
